import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useOverlaySSE from './useOverlaySSE';
import api from '../../lib/api';
import './overlay.css';

// --- Platform detection ---
function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}
function detectPlatform(url) {
  if (!url) return null;
  const ytId = extractYouTubeId(url);
  if (ytId) {
    if (url.includes('/shorts/')) return 'youtube_shorts';
    return 'youtube';
  }
  if (/tiktok\.com/.test(url)) return 'tiktok';
  return null;
}

export default function OverlayMediaShare() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [queue, setQueue] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [progress, setProgress] = useState(0);
  const playerRef = useRef(null);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const videoRef = useRef(null);
  const volume = parseFloat(searchParams.get('vol') || '1.0');
  const defaultDuration = parseInt(searchParams.get('dur') || '60');
  const accentColor = searchParams.get('accent') || '#FF6B35';

  const fetchQueue = useCallback(async () => {
    try {
      const res = await api.get(`/overlay/media/${token}`);
      setQueue(res.data || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    document.documentElement.classList.add('overlay-body');
    document.body.classList.add('overlay-body');
    fetchQueue();
    return () => {
      document.documentElement.classList.remove('overlay-body');
      document.body.classList.remove('overlay-body');
    };
  }, [fetchQueue]);

  const markPlayed = useCallback(async (orderId) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (progressRef.current) { clearInterval(progressRef.current); progressRef.current = null; }
    if (playerRef.current) { try { playerRef.current.destroy(); } catch {} playerRef.current = null; }
    setResolved(null);
    setProgress(0);

    if (orderId && orderId.startsWith('test-')) {
      setQueue(prev => prev.filter(m => m.order_id !== orderId));
      return;
    }
    try {
      await api.post(`/overlay/media/mark-played/${token}/${orderId}`);
      fetchQueue();
    } catch {}
  }, [token, fetchQueue]);

  const handleEvent = useCallback((event) => {
    if (event.type === 'media_added' || event.type === 'media_skipped') {
      if (event.is_test && event.media) {
        setQueue(prev => [...prev, event.media]);
      } else {
        fetchQueue();
      }
    }
  }, [fetchQueue]);

  useOverlaySSE(token, handleEvent);

  const current = queue[0];
  const platform = current ? detectPlatform(current.media_url) : null;

  // --- Resolve TikTok/IG via backend ---
  useEffect(() => {
    if (!current || platform === 'youtube') { setResolved(null); return; }
    if (!platform) return;
    const resolve = async () => {
      try {
        const res = await api.get(`/overlay/oembed?url=${encodeURIComponent(current.media_url)}`);
        setResolved(res.data);
      } catch { setResolved(null); }
    };
    resolve();
  }, [current, platform]);

  // --- Start progress bar timer ---
  const startProgressTimer = useCallback((totalSec) => {
    if (progressRef.current) clearInterval(progressRef.current);
    const startTime = Date.now();
    const totalMs = totalSec * 1000;
    setProgress(0);
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min((elapsed / totalMs) * 100, 100);
      setProgress(pct);
      if (pct >= 100) clearInterval(progressRef.current);
    }, 100);
  }, []);

  // --- YouTube Player ---
  useEffect(() => {
    if (!current || platform !== 'youtube') return;
    const videoId = extractYouTubeId(current.media_url);
    if (!videoId) return;

    const initPlayer = () => {
      if (!window.YT?.Player) { setTimeout(initPlayer, 300); return; }
      if (playerRef.current) { try { playerRef.current.destroy(); } catch {} playerRef.current = null; }
      const el = document.getElementById('media-yt-player');
      if (!el) return;

      playerRef.current = new window.YT.Player('media-yt-player', {
        width: '100%', height: '100%',
        videoId,
        playerVars: { autoplay: 1, controls: 0, modestbranding: 1, playsinline: 1, rel: 0 },
        events: {
          onReady: (e) => {
            e.target.setVolume(volume * 100);
            e.target.playVideo();
            // Start progress based on video duration
            const dur = e.target.getDuration() || defaultDuration;
            startProgressTimer(dur);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) {
              markPlayed(current.order_id);
            }
          }
        }
      });
    };
    initPlayer();
  }, [current, platform, volume, markPlayed, defaultDuration, startProgressTimer]);

  // --- TikTok: native <video> autoplay ---
  useEffect(() => {
    if (!current || platform !== 'tiktok' || !resolved?.video_url) return;
    const duration = current.duration || defaultDuration;
    startProgressTimer(duration);
    timerRef.current = setTimeout(() => markPlayed(current.order_id), duration * 1000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [current, platform, resolved, defaultDuration, markPlayed, startProgressTimer]);

  // --- Instagram: iframe with timer ---
  useEffect(() => {
    if (!current || platform !== 'instagram' || !resolved?.embed_url) return;
    const duration = current.duration || defaultDuration;
    startProgressTimer(duration);
    timerRef.current = setTimeout(() => markPlayed(current.order_id), duration * 1000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [current, platform, resolved, defaultDuration, markPlayed, startProgressTimer]);

  // Load YT IFrame API
  useEffect(() => {
    if (window.YT) return;
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  }, []);

  if (!current || !platform) return <div className="overlay-container" style={{ '--ov-accent': accentColor }} />;

  const isPortrait = platform !== 'youtube';
  const duration = current.duration || defaultDuration;

  const getPlayerClass = () => {
    if (platform === 'youtube') return 'media-share-player--landscape';
    return 'media-share-player--portrait';
  };

  return (
    <div className="overlay-container" style={{ '--ov-accent': accentColor }}>
      <div className="media-share-wrap" data-orientation={isPortrait ? 'portrait' : 'landscape'}>
        <div className="media-share-card">
          {!resolved && platform !== 'youtube' && (
             <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white gap-2 z-50">
               <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
               <span className="text-[10px] font-bold uppercase tracking-tighter">Memuat Video...</span>
             </div>
          )}
          {/* Player */}
          <div className={`media-share-player ${getPlayerClass()}`}>
            {(platform === 'youtube' || platform === 'youtube_shorts') && (
              <div id="media-yt-player" key={current.order_id} />
            )}
            {platform === 'tiktok' && resolved?.video_url && (
              <video
                ref={(el) => {
                  videoRef.current = el;
                  if (el) {
                    // Try unmuted play first
                    el.muted = false;
                    el.play().catch(() => {
                      // Fallback to muted if blocked
                      el.muted = true;
                      el.play();
                    });
                  }
                }}
                src={resolved.video_url}
                autoPlay
                playsInline
                controls={false}
                crossOrigin="anonymous"
                style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0 }}
                onEnded={() => markPlayed(current.order_id)}
                onLoadedData={(e) => { 
                  e.target.volume = volume;
                  // Final attempt to unmute after load
                  setTimeout(() => {
                    if (e.target.muted) {
                      e.target.muted = false;
                      e.target.play().catch(() => { e.target.muted = true; });
                    }
                  }, 1000);
                }}
              />
            )}
          </div>

          {/* Progress Bar */}
          <div className="media-share-progress">
            <div className="media-share-progress-fill" style={{ width: `${progress}%` }} />
          </div>

          {/* Info */}
          <div className="media-share-info">
            <span className="media-share-name">
              {platform === 'youtube' ? '▶️' : platform === 'tiktok' ? '🎵' : '📸'} {current.supporter_name}
            </span>
            <span className="media-share-queue">
              {queue.length > 1 ? `+${queue.length - 1} antrian` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
